import React from "react";
import useReputation from "../hooks/useReputation";

/**
 * AvatarFrame — wraps a circular avatar in the user's equipped cosmetic frame.
 *
 * Auto-fetches the user's reputation+cosmetics (batched + cached) and applies
 * the equipped frame's css class to a wrapper. If the user has no frame
 * equipped (or cosmetics still loading), renders just the inner avatar with
 * an optional fallback border via `noFrameClass`.
 *
 * Props:
 *   userId: string (required) — whose frame to look up
 *   size: number — outer pixel size (default 40)
 *   inset: number — how many px the inner avatar is inset from the frame (default 4)
 *   noFrameClass: string — fallback className for the inner avatar when no frame
 *   className: string — extra classes on the wrapper
 *   children: ReactNode — the avatar markup (an <img> or icon)
 *   testid: string — optional data-testid
 */
export default function AvatarFrame({
  userId,
  size = 40,
  inset = 4,
  noFrameClass = "",
  className = "",
  children,
  testid,
}) {
  const rep = useReputation(userId);
  const frame = rep?.cosmetics?.frame;
  const inner = size - inset * 2;
  const wrapperClass = `hg-frame ${frame?.css_class || ""} ${className}`.trim();
  const dataTitle = frame ? `${frame.label} · cosmetic frame` : undefined;
  return (
    <div
      className={wrapperClass}
      style={{ width: size, height: size }}
      data-testid={testid || (frame ? `avatar-frame-${frame.id}` : "avatar-frame")}
      data-frame={frame?.id || null}
      title={dataTitle}
    >
      <div
        className={`rounded-full overflow-hidden bg-zinc-800 grid place-items-center ${frame ? "" : noFrameClass}`}
        style={{ width: inner, height: inner, margin: "auto", position: "absolute", inset: inset }}
      >
        {children}
      </div>
    </div>
  );
}
